<template>
    <div>
        <el-card>
            <el-collapse v-if="modelValue"
                         v-model="collapseModel"
                         @change="handleChange">
                <el-collapse-item id="configuration-colapse"
                                  name="1">

                    <template #title>
                        <div class="title-box">
                            <div class="img-container">
                                <span class="material-symbols-outlined">settings</span>
                            </div>
                            <span class="config-value-combined">{{ configTittle }}</span>
                        </div>
                    </template>

                    <el-row :gutter="24">
                        <LanguagesSetup :modelValue="modelValue"
                                        :disabled="viewMode"
                                        @valueChanged="handleValueChanged" />

                        <FuzzyMatch :modelValue="modelValue"
                                    :disabled="viewMode"
                                    @valueChanged="handleValueChanged" />

                        <VariantsPopup :modelValue="modelValue"
                                       :disabled="viewMode"
                                       @valueChanged="handleValueChanged" />
                    </el-row>

                    <el-row :gutter="24">
                        <SubjectAndSubsubject :modelValue="modelValue"
                                              :disabled="viewMode"
                                              @valueChanged="handleValueChanged" />

                        <ProjectCode :modelValue="modelValue"
                                     :disabled="viewMode"
                                     @valueChanged="handleValueChanged" />

                        <ParagraphStyle :modelValue="modelValue"
                                       :disabled="viewMode"
                                       @valueChanged="handleValueChanged" />

                    </el-row>

                    <ExcelConfiguration :modelValue="modelValue"
                                        :disabled="!readOptionsConfigEditable || viewMode"
                                        :documentType="documentType"
                                        @configChanged="handleValueChanged" />

                    <PowerPointConfiguration :modelValue="modelValue"
                                             :disabled="!readOptionsConfigEditable || viewMode"
                                             :documentType="documentType"
                                             @configChanged="handleValueChanged" />

                    <IdmlConfiguration :modelValue="modelValue"
                                       :disabled="!readOptionsConfigEditable || viewMode"
                                       :documentType="documentType"
                                       @configChanged="handleValueChanged" />

                    <el-divider />

                    <el-row :gutter="24">
                        <ExternalProviders :modelValue="modelValue"
                                           :disabled="viewMode"
                                           @valueChanged="handleValueChanged" />
                    </el-row>

                    <slot></slot>
                </el-collapse-item>
            </el-collapse>
        </el-card>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed, ref, onBeforeUnmount, onMounted } from 'vue';
    import { useI18n } from 'vue-i18n';

    import { ConfigurationOptions, Poroject, Language, IdNamePair } from '@/models/ConfigurationOptions';
    import { TranslationConfiguration } from '@/models/TranslationConfiguration';
    import { DocumentType } from '@/models/DocumentType';

    import { translationConfigService } from './translationConfigService';

    import LanguagesSetup from './LanguagesSetup.vue';
    import FuzzyMatch from './FuzzyMatch.vue';
    import VariantsPopup from './VariantsPopup.vue';
    import ParagraphStyle from './ParagraphStyle.vue';
    import SubjectAndSubsubject from './SubjectAndSubsubject.vue';
    import ProjectCode from './ProjectCode.vue';
    import ExcelConfiguration from './ExcelConfiguration.vue';
    import PowerPointConfiguration from './PowerPointConfiguration.vue';
    import IdmlConfiguration from './IdmlConfiguration.vue';
    import ExternalProviders from './ExternalProviders.vue';

    const createDebounce = (fn: Function, delay: number) => {
        let timer: any = null;
        return (...args: any[]) => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                fn(...args);
                timer = null;
            }, delay);
        };
    };

    export default defineComponent({
        name: 'TranslationConfig',
        components: {
            LanguagesSetup,
            FuzzyMatch,
            VariantsPopup,
            ParagraphStyle,
            SubjectAndSubsubject,
            ProjectCode,
            ExcelConfiguration,
            PowerPointConfiguration,
            IdmlConfiguration,
            ExternalProviders,
        },
        props: {
            modelValue: {
                type: [Object, null] as PropType<TranslationConfiguration | null>,
                required: true
            },
            viewMode: {
                type: Boolean,
                default: false
            },
            readOptionsConfigEditable: {
                type: Boolean,
                default: false
            },
            pageNamePrefix: {
                type: String,
                required: true
            },
            documentType: {
                type: Number as PropType<DocumentType>,
                default: DocumentType.Docx
            },
            expended: {
                type: Boolean,
                default: false
            },
        },
        emits: ['update:modelValue', 'configChanged'],
        setup(props, { emit }) {
            const { t } = useI18n();
            const configService = translationConfigService.getInstance();
            const configurationOptions = ref<ConfigurationOptions | null>(null);
            const collapsedItems = ref<string[]>([]);
            const collapseModel = ref<string[]>(props.expended ? ['1'] : []);

            const cache = {
                sourceLanguage: computed(() => {
                    if (!configurationOptions.value || !props.modelValue) return '';
                    return configurationOptions.value.languages.find(
                        (l: Language) => l.id === props.modelValue?.sourceLanguageId
                    )?.name || '';
                }),
                targetLanguage: computed(() => {
                    if (!configurationOptions.value || !props.modelValue) return '';
                    return configurationOptions.value.languages.find(
                        (l: Language) => l.id === props.modelValue?.targetLanguageId
                    )?.name || '';
                }),
                subject: computed(() => {
                    if (!configurationOptions.value || !props.modelValue) return '';
                    return configurationOptions.value.subjects.find(
                        (s: IdNamePair) => s.id === props.modelValue?.subjectId
                    )?.name || '';
                }),
                projectCode: computed(() => {
                    if (!configurationOptions.value || !props.modelValue) return '';
                    const project = configurationOptions.value.projectCodes.find(
                        (p: Poroject) => p.code === props.modelValue?.projectCode
                    );
                    return project && project.id !== -1 ? ` ${project.display},` : '';
                })
            };

            const configTittle = computed(() => {
                if (collapsedItems.value.includes('1') || !configurationOptions.value) {
                    return t('translationConfig.configTitleShort', [props.pageNamePrefix]);
                }

                return t('translationConfig.configTitleLong', [
                    props.pageNamePrefix,
                    cache.sourceLanguage.value,
                    cache.targetLanguage.value,
                    cache.subject.value,
                    cache.projectCode.value,
                    props.modelValue?.minMatchPercent
                ]);
            });

            const debouncedEmit = createDebounce(() => {
                emit('configChanged');
            }, 1500);

            const handleValueChanged = () => {
                debouncedEmit();
            };

            const handleChange = (val: string[]) => {
                collapsedItems.value = val;
            };

            const init = async () => {
                configurationOptions.value = await configService.getConfigOptions();
            };

            onMounted(() => {
                init();
            });

            onBeforeUnmount(() => {
                if (debouncedEmit && typeof debouncedEmit === 'function' && 'timer' in debouncedEmit) {
                    clearTimeout((debouncedEmit as any).timer);
                }
            });

            return {
                configurationOptions,
                collapseModel,
                configTittle,
                handleValueChanged,
                handleChange,
                t
            };
        }
    });
</script>

<style lang="scss" scoped>
    .title-box {
        color: var(--text-color);
        display: flex;
        font-family: Nunito;
    }

    .config-value-combined {
        font-size: 19px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
</style>
